// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a message's text with its long pastes (features/pasted_text) folded where they were pasted.
// A marker line only folds when the message carries the file it names; its file then stays out of
// the attachments under the message (pastedAttachmentNames).

import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import type {MarkdownParseOptions} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import {pastedTextNames, splitPastedText} from '@app/features/pasted_text/PastedText';
import {PastedTextFold, type PastedTextSource} from '@app/features/pasted_text/PastedTextFold';
import styles from '@app/features/pasted_text/PastedTextFold.module.css';
import type {MessageAttachment} from '@konvo/schema/src/domains/message/MessageResponseSchemas';

type Attachments = ReadonlyArray<Pick<MessageAttachment, 'id' | 'filename' | 'size' | 'url' | 'proxy_url'>> | undefined;

/** The pasted files a message shows as folds in its text: marked in it and attached to it. */
export function pastedAttachmentNames(content: string, attachments: Attachments): ReadonlySet<string> {
	if (!attachments?.length) return new Set();
	const folded = pastedTextNames(content);
	return new Set(attachments.filter((file) => folded.has(file.filename)).map((file) => file.filename));
}

function attachmentSource(attachments: Attachments, name: string): PastedTextSource | null {
	const file = attachments?.find((attachment) => attachment.filename === name);
	const url = file?.url ?? file?.proxy_url;
	if (!file || !url) return null;
	return {
		key: url,
		name,
		size: file.size,
		loadBlob: async () => {
			const response = await fetch(url, {credentials: 'omit'});
			return response.ok ? response.blob() : null;
		},
	};
}

export function MessagePastedText({
	content,
	attachments,
	options,
	flx,
}: {
	content: string;
	attachments: Attachments;
	options?: MarkdownParseOptions;
	flx: string;
}) {
	const pasted = pastedAttachmentNames(content, attachments);
	if (pasted.size === 0) return <SafeMarkdown content={content} options={options} data-flx={flx} />;
	return (
		<>
			{splitPastedText(content, pasted).map((part, index) =>
				part.kind === 'paste' ? (
					<PastedTextFold
						key={index}
						source={attachmentSource(attachments, part.name)}
						className={styles.inMessage}
						flx="channel.message.pasted-text"
					/>
				) : (
					<SafeMarkdown key={index} content={part.text} options={options} data-flx={flx} />
				),
			)}
		</>
	);
}
